import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Download, Search, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { Pagination } from '../../components/admin/Pagination';
import { SortableTh, TableSkeleton } from '../../components/admin/SortableTable';
import { TableEmpty } from '../../components/admin/TableEmpty';
import { useDebounce } from '../../hooks/useDebounce';

export default function AdminNewsletter() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState({ by: 'created_at', dir: 'desc' });
    const [loading, setLoading] = useState(true);
    const PAGE_SIZE = 10;
    const debouncedSearch = useDebounce(search);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/newsletter', { params: { page, page_size: PAGE_SIZE, search: debouncedSearch || undefined, sort_by: sort.by, sort_dir: sort.dir } });
            setItems(r.data.items);
            setTotal(r.data.total);
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [page, debouncedSearch, sort]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setPage(1); }, [debouncedSearch, sort]);

    const remove = async (id) => {
        try { await api.delete(`/admin/newsletter/${id}`); toast.success('Removed.'); await load(); }
        catch { toast.error('Failed'); }
    };

    const exportCsv = async () => {
        try {
            const r = await api.get('/admin/newsletter', { params: { page: 1, page_size: 100, search: debouncedSearch || undefined } });
            const all = [];
            let p = 1;
            const totalCount = r.data.total;
            all.push(...r.data.items);
            while (all.length < totalCount) {
                p += 1;
                const next = await api.get('/admin/newsletter', { params: { page: p, page_size: 100, search: debouncedSearch || undefined } });
                if (!next.data.items.length) break;
                all.push(...next.data.items);
            }
            const rows = ['email,subscribed_at', ...all.map((i) => `${i.email},${i.created_at}`)];
            const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'byanfal-subscribers.csv'; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('Export failed'); }
    };

    return (
        <div data-testid="admin-newsletter-page">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                    <h1 className="font-serif text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Newsletter</h1>
                </div>
                <button onClick={exportCsv} data-testid="admin-newsletter-export" className="btn-outline-burgundy px-5 py-2.5 rounded-xl text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2"><Download className="w-4 h-4" /> Export CSV</button>
            </div>

            <div className="mb-6 relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-muted)' }} />
                <input
                    data-testid="admin-newsletter-search-input"
                    type="text"
                    placeholder="Search by email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="input-luxury pl-9"
                />
            </div>

            <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]" data-testid="admin-newsletter-table">
                    <thead className="bg-[var(--brand-rose)]/40">
                        <tr className="text-left"><SortableTh label="Email" field="email" sort={sort} onSort={setSort} /><SortableTh label="Subscribed" field="created_at" sort={sort} onSort={setSort} /><th className="p-4 text-right">Actions</th></tr>
                    </thead>
                    {loading ? <TableSkeleton cols={3} /> : (
                    <tbody>
                        {items.map((s) => (
                            <tr key={s.id} className="border-t border-[rgba(90,36,50,0.08)] hover:bg-[var(--brand-rose)]/20 transition-colors" data-testid={`admin-newsletter-row-${s.id}`}>
                                <td className="p-4">{s.email}</td>
                                <td className="p-4 text-xs" style={{ color: 'var(--ink-muted)' }}>{new Date(s.created_at).toLocaleString()}</td>
                                <td className="p-4 text-right"><button onClick={() => remove(s.id)} className="p-2 hover:bg-[var(--brand-rose)]/50 rounded"><Trash2 className="w-4 h-4" style={{ color: 'var(--brand-burgundy)' }} /></button></td>
                            </tr>
                        ))}
                    </tbody>
                    )}
                </table>
                </div>
                {!loading && items.length === 0 && (
                    <TableEmpty icon={Mail} title="No subscribers yet" subtitle="New newsletter sign-ups will appear here." testId="admin-newsletter-empty" />
                )}
            </div>

            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testIdPrefix="admin-newsletter-pagination" />
        </div>
    );
}
